import React, { useState, useEffect } from 'react';
import { ReviewFilters, reviewAutomationService } from '../services/api/reviewAutomation.service';

interface ReviewFiltersPanelProps {
  filters: ReviewFilters;
  onFiltersChange: (filters: ReviewFilters) => void;
  serverId?: number;
}

export const ReviewFiltersPanel: React.FC<ReviewFiltersPanelProps> = ({
  filters,
  onFiltersChange,
  serverId
}) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [localFilters, setLocalFilters] = useState<ReviewFilters>(filters);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await reviewAutomationService.getChangeCategories(serverId);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();
  }, [serverId]);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof ReviewFilters, value: any) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters: ReviewFilters = {
      server_id: serverId
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        <button
          onClick={clearFilters}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Clear All
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Entity Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Entity Type
          </label>
          <select
            value={localFilters.entity_type || ''}
            onChange={(e) => handleFilterChange('entity_type', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="supervisor">Supervisor</option>
            <option value="controller">Controller</option>
          </select>
        </div>

        {/* Change Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Change Type
          </label>
          <select
            value={localFilters.change_type || ''}
            onChange={(e) => handleFilterChange('change_type', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Changes</option>
            <option value="new">New</option>
            <option value="updated">Updated</option>
          </select>
        </div>

        {/* Review Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Review Status
          </label>
          <select
            value={localFilters.review_status || ''}
            onChange={(e) => handleFilterChange('review_status', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={localFilters.category || ''}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        {/* Supervisor Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Supervisor Name
          </label>
          <input
            type="text"
            value={localFilters.supervisor_name || ''}
            onChange={(e) => handleFilterChange('supervisor_name', e.target.value)}
            placeholder="Search supervisor..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Server ID (if not provided as prop) */}
        {!serverId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Server ID
            </label>
            <input
              type="number"
              value={localFilters.server_id || ''}
              onChange={(e) => handleFilterChange('server_id', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="Server ID..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      {/* Active Filters Display */}
      <div className="mt-4">
        <div className="flex flex-wrap gap-2">
          {Object.entries(localFilters).map(([key, value]) => {
            if (!value || key === 'server_id') return null;
            
            return (
              <span
                key={key}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {key.replace(/_/g, ' ')}: {value}
                <button
                  onClick={() => handleFilterChange(key as keyof ReviewFilters, undefined)}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none"
                >
                  <svg className="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                    <path strokeLinecap="round" strokeWidth="1.5" d="m1 1 6 6m0-6L1 7" />
                  </svg>
                </button>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};